import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, LogOut, KeyRound } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { fieldLogin, setFieldSession, formatApiError } from "@/lib/api";

export function FieldAccessDialog({ open, onOpenChange, onSession }) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const s = await fieldLogin(code, name);
      setFieldSession(s); onSession(s); onOpenChange(false); setCode("");
      toast.success(`Field mode active · ${s.name}`);
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="field-access-dialog" className="bg-[#06163d] border-border text-slate-100 max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-wide flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-cyan" /> Field Team Sign-in
          </DialogTitle>
          <DialogDescription className="font-mono-t text-[11px] text-slate-400">
            Enter the shared team access code. Every report is stamped with your name and time.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col gap-3">
          <input data-testid="field-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name / unit"
            className="bg-slate-900/70 border border-border rounded-md px-3 py-2 text-sm font-mono-t focus:outline-none focus:border-cyan" />
          <input data-testid="field-code-input" type="password" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Team access code"
            className="bg-slate-900/70 border border-border rounded-md px-3 py-2 text-sm font-mono-t focus:outline-none focus:border-cyan" />
          {err && <div data-testid="field-login-error" className="font-mono-t text-[11px] text-risk-critical">{err}</div>}
          <button data-testid="field-login-submit" disabled={busy || name.trim().length < 2 || !code}
            className="bg-cyan text-slate-950 font-heading font-semibold uppercase tracking-wide text-sm py-2 rounded-md hover:bg-cyan/85 transition-colors disabled:opacity-50">
            {busy ? "Verifying…" : "Enter field mode"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function FieldBadge({ session, onSignIn, onSignOut }) {
  if (!session) {
    return (
      <button data-testid="field-signin-button" onClick={onSignIn}
        className="glass flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-border font-mono-t text-[10px] uppercase tracking-wide text-slate-200 hover:border-cyan/60 hover:text-cyan transition-colors">
        <KeyRound className="w-3.5 h-3.5" /> Field sign-in
      </button>
    );
  }
  return (
    <div data-testid="field-session-badge" className="glass flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-emerald-500/50 font-mono-t text-[10px] uppercase tracking-wide text-emerald-300">
      <ShieldCheck className="w-3.5 h-3.5" /> {session.name}
      <button data-testid="field-signout-button" onClick={onSignOut} className="ml-1 text-slate-400 hover:text-risk-critical" title="Sign out">
        <LogOut className="w-3 h-3" />
      </button>
    </div>
  );
}
